import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Col, Button } from 'react-bootstrap-5';

import CriteriaDecorator from '../helpers/criteria_decorator';
import PopoverSelect from './filters/popover_select';
import LocationInput from './filters/inputs/location_input';
import PropertyTypeInput from './filters/inputs/property_type_input';
import RangeInput from './filters/inputs/range_input';
import OffMarketFiltersModal from './filters/off_market_filters_modal';
import SortingSelect from './filters/sorting_select';
import SessionModal from '../../session_modal';
import EmbeddedMode from '../../embedded_mode';
import ProductFeature from './product_feature';

import { clearFilters } from 'bundles/connect/components/store/actions/criteria';
import { showSaveSearchModal } from 'bundles/connect/components/store/actions/ui';
import type { Criteria } from 'bundles/connect/types';
import EnrichRethinkList from '../../off_market_properties/add_to_rethink/list_enrichment/enrich_rethink_list';

type OffMarketFiltersProps = {
  clearFilters: () => void;
  criteria: Criteria;
  offMarketSearch: boolean;
  profile: {
    id?: number;
    userType?: string;
  };
  showSaveSearchModal: (show: boolean) => void;
}

function OffMarketFilters(
  { clearFilters, criteria, offMarketSearch, profile, showSaveSearchModal }: OffMarketFiltersProps
) {
  const [showAllFilters, setShowAllFilters] = React.useState(false);

  const decoratedCriteria = useMemo(
    () => new CriteriaDecorator(criteria, offMarketSearch), [criteria, offMarketSearch]
  );

  const filterCount = decoratedCriteria.numCriteriaExcludingLocation();

  return (
    <>
      <Col lg={3} md={4}>
        <LocationInput />
      </Col>
      <Col className="d-none d-xl-block" md="auto" style={{ minWidth: '180px' }} xs={6}>
        <PopoverSelect
          bodyClassName="p-0"
          className="border"
          label="Property Type"
          selectedLabel={decoratedCriteria.assetClassSummary(true)}
        >
          <PropertyTypeInput />
        </PopoverSelect>
      </Col>
      <Col className="d-none d-xl-block" xs="auto">
        <PopoverSelect
          label="Number of Units"
          selectedLabel={decoratedCriteria.numberOfUnitsSummary()}
        >
          <RangeInput
            maxAttribute="maxNumberOfUnits"
            minAttribute="minNumberOfUnits"
            placeholder=""
            type="number"
          />
        </PopoverSelect>
      </Col>
      <Col className="d-none d-xl-block" xs="auto">
        <PopoverSelect
          label="Building Size"
          selectedLabel={decoratedCriteria.buildingSizeSummary()}
        >
          <RangeInput
            maxAttribute="maxBuildingSizeSf"
            minAttribute="minBuildingSizeSf"
            placeholder="SF"
            type="number"
          />
        </PopoverSelect>
      </Col>
      <Col className="d-none d-xl-block" xs="auto">
        <PopoverSelect
          label="Lot Size"
          selectedLabel={decoratedCriteria.lotSizeSummary()}
        >
          <RangeInput
            float
            maxAttribute="maxLotSizeAcres"
            minAttribute="minLotSizeAcres"
            placeholder="Acres"
          />
        </PopoverSelect>
      </Col>
      <Col xs="auto">
        <SortingSelect />
      </Col>
      <Col md="auto" xs={6}>
        <Button
          data-pendo-tag="all_properties_all_filters"
          variant="outline-primary"
          onClick={() => setShowAllFilters(true)}
        >
          All Filters
          {filterCount > 0 && <span> ({filterCount})</span>}
        </Button>
      </Col>
      <EmbeddedMode>
        <ProductFeature product="prospecting">
          <Col className="d-none d-md-block" xs="auto">
            <Button
              data-pendo-tag="all_properties_save_search"
              variant="secondary"
              onClick={() => showSaveSearchModal(true)}
            >
              Save Search
            </Button>
          </Col>
        </ProductFeature>
      </EmbeddedMode>
      {filterCount > 0 && (
        <Col className="d-none d-md-block" md="auto" xs={6}>
          <Button className="px-0" variant="text-neutral" onClick={clearFilters}>Clear Filters</Button>
        </Col>
      )}
      {profile?.id && (
        <OffMarketFiltersModal
          show={showAllFilters}
          onHide={() => setShowAllFilters(false)}
        />
      )}
      {!profile?.id && showAllFilters && (
        <SessionModal onHide={() => setShowAllFilters(false)} />
      )}
      <EmbeddedMode display>
        <Col className="d-none d-md-block ms-auto" xs="auto">
          <EnrichRethinkList />
        </Col>
      </EmbeddedMode>
    </>
  );
}

const mapStateToProps = ({ criteria, ui: { offMarketSearch, profile } }) => {
  return { criteria, offMarketSearch, profile };
};

export default connect(mapStateToProps, { clearFilters, showSaveSearchModal })(OffMarketFilters);
